import {
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { DonutSegment } from './DonutSegment';
import { DonutTooltip } from './DonutTooltip';
import { HighlightRing } from './HighlightRing';
import {
  calculateSegmentOffset,
  DEFAULT_SEGMENT_GAP,
  DONUT_CENTER,
  DONUT_RADIUS,
  resolveColor,
} from './utils/donut';

type SegmentData = {
  percentage: number;
  color?: string;
  label?: string;
  formattedValue?: string;
  breakdownRows?: {
    label: string;
    value: string;
  }[];
};

type Props = {
  children: ReactNode;
  segments: SegmentData[];
  segmentGap?: number;
  donutWidth?: number;
  donutHeight?: number;
  strokeWidth?: number;
  circleSizeX?: number;
  circleSizeY?: number;
  interactive?: boolean;
};

const VIEWBOX = `0 0 ${DONUT_CENTER * 2} ${DONUT_CENTER * 2}`;

export function Donut({
  children,
  segments,
  segmentGap = DEFAULT_SEGMENT_GAP,
  donutWidth,
  donutHeight,
  strokeWidth = 2.6,
  circleSizeX = 224,
  circleSizeY = 224,
  interactive = false,
}: Props) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!interactive || typeof window === 'undefined') return undefined;

    // Dismiss tooltip when tapping outside the donut on touch devices
    const handlePointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;

      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setHoveredIndex(null);
        setMousePos(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [interactive]);

  const handlePointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!interactive) return;

    setMousePos({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handlePointerLeave = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!interactive) return;

    // On touch devices the finger lifting fires pointerleave — don't dismiss,
    // the tooltip should stay until the user taps elsewhere.
    if (e.pointerType === 'touch') return;

    setHoveredIndex(null);
    setMousePos(null);
  };

  const hoveredSegment = hoveredIndex === null ? null : segments[hoveredIndex];
  const hoveredOffset =
    hoveredIndex === null ? 0 : calculateSegmentOffset(segments, hoveredIndex);

  return (
    <CircleContainer
      ref={containerRef}
      style={{ width: donutWidth ?? 238, height: donutHeight ?? 238 }}
    >
      <svg
        viewBox={VIEWBOX}
        width={circleSizeX}
        height={circleSizeY}
        onPointerMove={interactive ? handlePointerMove : undefined}
        onPointerLeave={interactive ? handlePointerLeave : undefined}
      >
        <circle
          className={'donut-hole'}
          cx={DONUT_CENTER}
          cy={DONUT_CENTER}
          r={DONUT_RADIUS}
          fill={'#FFFFFF'}
          opacity={'0.5'}
        />
        <circle
          className={'donut-ring'}
          cx={DONUT_CENTER}
          cy={DONUT_CENTER}
          r={DONUT_RADIUS}
          fill={'transparent'}
          stroke={segments.length > 0 ? 'white' : 'gray'}
          strokeWidth={strokeWidth}
        />
        {segments.map((segment, index) => (
          <DonutSegment
            key={`segment-${index}`}
            segmentGap={segmentGap}
            color={segment.color}
            isFirst={index === 0}
            percentage={segment.percentage}
            strokeWidth={strokeWidth}
            dimmed={
              interactive && hoveredIndex !== null && hoveredIndex !== index
            }
            offset={
              index === 0 ? undefined : calculateSegmentOffset(segments, index)
            }
            onPointerEnter={
              interactive
                ? (e: ReactPointerEvent) => {
                    setHoveredIndex(index);
                    setMousePos({ x: e.clientX, y: e.clientY });
                  }
                : undefined
            }
            onPointerLeave={
              interactive
                ? (e: ReactPointerEvent) => {
                    // Only clear tooltip on desktop (mouse leave).
                    // On touch, keep tooltip until next interaction.
                    if (e.pointerType !== 'touch') {
                      setHoveredIndex(null);
                      setMousePos(null);
                    }
                  }
                : undefined
            }
          />
        ))}
        {interactive && hoveredSegment && (
          <HighlightRing
            percentage={hoveredSegment.percentage}
            offset={hoveredOffset}
            segmentGap={segmentGap}
            color={hoveredSegment.color}
            isFirst={hoveredIndex === 0}
          />
        )}
      </svg>
      <AbsoluteWrapper>{children}</AbsoluteWrapper>
      {interactive && hoveredSegment && mousePos && (
        <DonutTooltip
          formattedValue={hoveredSegment.formattedValue}
          label={hoveredSegment.label}
          breakdownRows={hoveredSegment.breakdownRows}
          mousePos={mousePos}
          isLeftSide={
            containerRef.current
              ? mousePos.x <
                containerRef.current.getBoundingClientRect().left +
                  containerRef.current.getBoundingClientRect().width / 2
              : false
          }
          resolvedColor={resolveColor(
            hoveredSegment.color,
            containerRef.current,
          )}
        />
      )}
    </CircleContainer>
  );
}

const CircleContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  text-align: center;
  font-weight: bold;
  width: 100%;
  svg {
    transform: rotate(-90deg);
  }
`;

const AbsoluteWrapper = styled.div`
  position: absolute;
  width: 100%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;
